import { useState, useEffect, useCallback } from 'react';
import logger from '../lib/logger';
import { useAuth } from '../context/AuthContext';
import { cashRegisterAPI, reportsAPI, cashCutsAPI, salesAPI } from '../lib/api';
import { formatCurrency, formatDateTime, paymentMethodLabels, cn } from '../lib/utils';
import printerService from '../lib/printerService';
import { buildSaleReceipt } from '../lib/escpos';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  PiggyBank, 
  Lock,
  Unlock,
  ArrowUpCircle,
  ArrowDownCircle,
  Loader2,
  AlertTriangle,
  History,
  Receipt,
  TrendingDown,
  Wallet,
  ScrollText,
  Printer,
  DollarSign,
  ShieldAlert,
  ShieldCheck,
  ShieldX,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';

export default function CajaPage() {
  const { selectedBusiness, user } = useAuth();
  const canEditTotal = user?.is_master_admin || user?.role === 'admin';
  const [registers, setRegisters] = useState([]);
  const [selectedRegister, setSelectedRegister] = useState(null);
  const [movements, setMovements] = useState([]);
  const [openSales, setOpenSales] = useState([]);
  const [openSummary, setOpenSummary] = useState(null);
  const [todayExpenses, setTodayExpenses] = useState([]);
  const [todayMovements, setTodayMovements] = useState([]);
  const [todayActivitySummary, setTodayActivitySummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [auditSale, setAuditSale] = useState(null);
  const [auditEntries, setAuditEntries] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);

  // Edit-total request dialog
  const [editTotalSale, setEditTotalSale] = useState(null);
  const [newTotalInput, setNewTotalInput] = useState('');
  const [reasonInput, setReasonInput] = useState('');
  const [editSubmitting, setEditSubmitting] = useState(false);

  // Cancel-sale request dialog
  const [cancelSale, setCancelSale] = useState(null);
  const [cancelReasonInput, setCancelReasonInput] = useState('');
  const [cancelSubmitting, setCancelSubmitting] = useState(false);
  
  const [openDialog, setOpenDialog] = useState(false);
  const [closeDialog, setCloseDialog] = useState(false);
  const [movementDialog, setMovementDialog] = useState(false);
  const [movementType, setMovementType] = useState('withdrawal');
  
  const [openingAmount, setOpeningAmount] = useState(0);
  const [closingAmount, setClosingAmount] = useState(0);
  const [movementAmount, setMovementAmount] = useState(0);
  const [notes, setNotes] = useState('');

  const loadData = useCallback(async () => {
    if (!selectedBusiness) return;
    
    try {
      setLoading(true);
      const [registersRes, openRes, activityRes] = await Promise.all([
        cashRegisterAPI.getAll(),
        reportsAPI.getOpenSales().catch(() => ({ data: { sales: [], summary: null } })),
        reportsAPI.getTodayActivity().catch(() => ({ data: { expenses: [], movements: [], summary: null } })),
      ]);
      setRegisters(registersRes.data);
      setOpenSales(openRes.data?.sales || []);
      setOpenSummary(openRes.data?.summary || null);
      setTodayExpenses(activityRes.data?.expenses || []);
      setTodayMovements(activityRes.data?.movements || []);
      setTodayActivitySummary(activityRes.data?.summary || null);
      
      if (registersRes.data.length > 0) {
        const detailRes = await cashRegisterAPI.get(registersRes.data[0].id);
        setSelectedRegister(detailRes.data.register);
        setMovements(detailRes.data.movements);
      }
    } catch (error) {
      logger.error('Error loading cash register:', error);
      toast.error('Error al cargar caja');
    } finally {
      setLoading(false);
    }
  }, [selectedBusiness]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleOpenRegister = async () => {
    if (openingAmount < 0) {
      toast.error('El monto inicial debe ser mayor o igual a 0');
      return;
    }

    try {
      await cashRegisterAPI.open({
        business_id: selectedBusiness.id,
        cash_register_id: selectedRegister.id,
        opening_amount: openingAmount,
        notes
      });
      toast.success('Caja abierta exitosamente');
      setOpenDialog(false);
      setOpeningAmount(0);
      setNotes('');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al abrir caja');
    }
  };

  const handleChangePaymentMethod = async (sale, newMethod) => {
    if (sale.payment_method === newMethod) return;
    try {
      await salesAPI.updatePaymentMethod(sale.id, newMethod);
      toast.success(
        `Método actualizado: ${paymentMethodLabels[sale.payment_method] || sale.payment_method} → ${paymentMethodLabels[newMethod] || newMethod}`
      );
      loadData();
    } catch (error) {
      // Surface the real cause so production issues are diagnosable
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      const fallback = error.message || 'Error desconocido';
      logger.error('Error updating payment method:', { status, detail, response: error.response?.data, error });
      const human = typeof detail === 'string'
        ? detail
        : Array.isArray(detail) && detail[0]?.msg
          ? detail[0].msg
          : fallback;
      toast.error(
        status ? `No se pudo actualizar (${status}): ${human}` : `No se pudo actualizar: ${human}`
      );
    }
  };

  const openAuditLog = async (sale) => {
    setAuditSale(sale);
    setAuditEntries([]);
    setAuditLoading(true);
    try {
      const { data } = await salesAPI.getAudits(sale.id);
      setAuditEntries(data?.audits || []);
    } catch (error) {
      logger.error('Error loading audits:', error);
      toast.error('No se pudo cargar la auditoría');
    } finally {
      setAuditLoading(false);
    }
  };

  const openEditTotal = (sale) => {
    setEditTotalSale(sale);
    setNewTotalInput(String(sale.total ?? ''));
    setReasonInput('');
  };

  const closeEditTotal = () => {
    setEditTotalSale(null);
    setNewTotalInput('');
    setReasonInput('');
  };

  const submitEditTotal = async () => {
    if (!editTotalSale) return;
    const parsed = parseFloat(newTotalInput);
    if (Number.isNaN(parsed) || parsed < 0) {
      toast.error('Ingresa un monto válido');
      return;
    }
    if (Math.abs(parsed - Number(editTotalSale.total || 0)) < 0.001) {
      toast.error('El nuevo monto es igual al actual');
      return;
    }
    if (!reasonInput.trim() || reasonInput.trim().length < 3) {
      toast.error('El motivo es obligatorio (mínimo 3 caracteres)');
      return;
    }
    setEditSubmitting(true);
    try {
      await salesAPI.requestTotalChange(editTotalSale.id, parsed, reasonInput.trim());
      toast.success('Solicitud enviada. El master admin debe autorizarla.');
      closeEditTotal();
      loadData();
    } catch (error) {
      const detail = error.response?.data?.detail || error.message || 'Error';
      toast.error(detail);
    } finally {
      setEditSubmitting(false);
    }
  };

  const openCancelSale = (sale) => {
    setCancelSale(sale);
    setCancelReasonInput('');
  };

  const closeCancelSale = () => {
    setCancelSale(null);
    setCancelReasonInput('');
  };

  const submitCancelSale = async () => {
    if (!cancelSale) return;
    const reason = cancelReasonInput.trim();
    if (reason.length < 3) {
      toast.error('El motivo es obligatorio (mínimo 3 caracteres)');
      return;
    }
    setCancelSubmitting(true);
    try {
      await salesAPI.requestCancel(cancelSale.id, reason);
      toast.success('Venta cancelada. Pendiente de autorización del master admin.');
      closeCancelSale();
      loadData();
    } catch (error) {
      const detail = error.response?.data?.detail || error.message || 'Error';
      toast.error(detail);
    } finally {
      setCancelSubmitting(false);
    }
  };


  const [reprintingId, setReprintingId] = useState(null);

  const handleReprintSale = async (saleSummary) => {
    if (!printerService.getSavedFingerprint()) {
      toast.error('Conecta una impresora en Configuración → Impresora');
      return;
    }
    setReprintingId(saleSummary.id);
    try {
      // The row only has summary fields — fetch the full sale (with items) before printing
      const { data: fullSale } = await salesAPI.get(saleSummary.id);
      if (!printerService.isConnected()) {
        await printerService.reconnectPrinter();
      }
      const bytes = buildSaleReceipt({
        business: selectedBusiness,
        sale: fullSale,
        clientName: fullSale.client_name || saleSummary.client_name || '',
      });
      await printerService.printRaw(bytes);
      toast.success(`Ticket #${fullSale.ticket_number || saleSummary.ticket_number || ''} reimpreso`);
    } catch (err) {
      logger.error('Reprint error:', err);
      toast.error(err?.message || 'No se pudo reimprimir el ticket');
    } finally {
      setReprintingId(null);
    }
  };

  const handleCloseRegister = async () => {
    try {
      const response = await cashRegisterAPI.close({
        cash_register_id: selectedRegister.id,
        closing_amount: closingAmount,
        notes
      });
      
      const diff = response.data.difference;

      // Archive today's open sales into a cash cut so the daily history
      // clears out of the main register view and moves to "Historial".
      try {
        await cashCutsAPI.close(notes);
      } catch (cutError) {
        // Don't fail the whole flow if there are no open sales to close
        const detail = cutError.response?.data?.detail || '';
        if (!detail.toLowerCase().includes('no hay ventas')) {
          logger.error('Error closing cash cut:', cutError);
        }
      }

      if (diff !== 0) {
        toast.warning(`Caja cerrada con diferencia de ${formatCurrency(diff)}`);
      } else {
        toast.success('Caja cerrada correctamente');
      }
      
      setCloseDialog(false);
      setClosingAmount(0);
      setNotes('');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cerrar caja');
    }
  };

  const handleMovement = async () => {
    if (movementAmount <= 0) {
      toast.error('El monto debe ser mayor a 0');
      return;
    }

    try {
      if (movementType === 'withdrawal') {
        await cashRegisterAPI.withdrawal({
          cash_register_id: selectedRegister.id,
          type: 'withdrawal',
          amount: movementAmount,
          description: notes
        });
        toast.success('Retiro registrado');
      } else {
        await cashRegisterAPI.deposit({
          cash_register_id: selectedRegister.id,
          type: 'deposit',
          amount: movementAmount,
          description: notes
        });
        toast.success('Depósito registrado');
      }
      
      setMovementDialog(false);
      setMovementAmount(0);
      setNotes('');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al registrar movimiento');
    }
  };

  const openMovementDialog = (type) => {
    setMovementType(type);
    setMovementAmount(0);
    setNotes('');
    setMovementDialog(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-slate-500" />
      </div>
    );
  }

  const isOpen = selectedRegister?.status === 'open';

  return (
    <div className="space-y-6 animate-fade-in" data-testid="caja-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>
            Caja
          </h1>
          <p className="text-slate-500 mt-1">
            Control de caja de {selectedBusiness?.name}
          </p>
        </div>
        {isOpen ? (
          <div className="flex gap-2">
            <Button 
              variant="outline"
              onClick={() => openMovementDialog('withdrawal')}
              className="border-slate-200"
            >
              <ArrowDownCircle className="w-4 h-4 mr-2 text-red-400" />
              Retiro
            </Button>
            <Button 
              variant="outline"
              onClick={() => openMovementDialog('deposit')}
              className="border-slate-200"
            >
              <ArrowUpCircle className="w-4 h-4 mr-2 text-emerald-400" />
              Depósito
            </Button>
            <Button 
              onClick={() => {
                setClosingAmount(selectedRegister.current_amount);
                setCloseDialog(true);
              }}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              <Lock className="w-4 h-4 mr-2" />
              Cerrar caja
            </Button>
          </div>
        ) : (
          <Button 
            onClick={() => setOpenDialog(true)}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="open-register-btn"
          >
            <Unlock className="w-4 h-4 mr-2" />
            Abrir caja
          </Button>
        )}
      </div>

      {/* Status Card */}
      <Card className={cn(
        "border",
        isOpen ? "border-emerald-500/30 bg-emerald-500/10" : "border-slate-200 bg-white"
      )}>
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className={cn(
                "p-4 rounded-xl",
                isOpen ? "bg-emerald-500/20" : "bg-slate-100"
              )}>
                <PiggyBank className={cn(
                  "w-8 h-8",
                  isOpen ? "text-emerald-400" : "text-slate-500"
                )} />
              </div>
              <div>
                <h2 className="text-xl font-bold" style={{ fontFamily: 'Manrope' }}>
                  {selectedRegister?.name || 'Caja Principal'}
                </h2>
                <Badge 
                  variant="outline" 
                  className={cn(
                    "mt-1",
                    isOpen 
                      ? "text-emerald-400 border-emerald-500/30" 
                      : "text-slate-500 border-slate-200"
                  )}
                >
                  {isOpen ? 'Abierta' : 'Cerrada'}
                </Badge>
              </div>
            </div>

            {isOpen && (
              <div className="grid grid-cols-3 gap-6">
                <div className="text-center">
                  <p className="text-sm text-slate-500">Inicial</p>
                  <p className="text-xl font-bold" style={{ fontFamily: 'Manrope' }}>
                    {formatCurrency(selectedRegister.opening_amount)}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-sm text-slate-500">Actual</p>
                  <p className="text-2xl font-bold text-emerald-400" style={{ fontFamily: 'Manrope' }}>
                    {formatCurrency(selectedRegister.current_amount)}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-sm text-slate-500">Esperado</p>
                  <p className="text-xl font-bold" style={{ fontFamily: 'Manrope' }}>
                    {formatCurrency(selectedRegister.expected_amount)}
                  </p>
                </div>
              </div>
            )}

            {!isOpen && selectedRegister?.difference !== undefined && selectedRegister.difference !== 0 && (
              <div className="flex items-center gap-2 text-amber-400">
                <AlertTriangle className="w-5 h-5" />
                <span>Último cierre con diferencia: {formatCurrency(selectedRegister.difference)}</span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Ventas del día (open sales not yet archived in a cash cut) */}
      <Card className="border-slate-200 bg-white" data-testid="open-sales-card">
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <Receipt className="w-5 h-5" />
              Ventas del día
            </CardTitle>
            {openSummary && openSummary.total_transactions > 0 && (
              <div className="flex items-center gap-4 text-sm">
                <span className="text-slate-500">
                  {openSummary.total_transactions} {openSummary.total_transactions === 1 ? 'venta' : 'ventas'}
                </span>
                {openSummary.total_tips > 0 && (
                  <span
                    className="text-emerald-600 flex items-center gap-1"
                    data-testid="open-sales-tips"
                    title="Propinas totales cobradas hoy"
                  >
                    <span className="text-xs text-emerald-700/70">Propinas</span>
                    <span className="font-semibold">
                      + {formatCurrency(openSummary.total_tips)}
                    </span>
                  </span>
                )}
                <span className="font-bold text-emerald-600" data-testid="open-sales-total">
                  {formatCurrency(openSummary.total_sales)}
                </span>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {openSales.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 text-slate-500" data-testid="open-sales-empty">
              <Receipt className="w-12 h-12 mb-3 opacity-50" />
              <p>No hay ventas abiertas hoy</p>
              <p className="text-xs mt-1">Las ventas aparecerán aquí hasta que cierres la caja</p>
            </div>
          ) : (
            <Table className="data-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Hora</TableHead>
                  <TableHead>Ticket</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Barbero</TableHead>
                  <TableHead className="text-center">Items</TableHead>
                  <TableHead>Método</TableHead>
                  <TableHead className="text-right">Propina</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead className="text-right w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {openSales.map((sale, index) => (
                  <TableRow
                    key={sale.id}
                    className="animate-fade-in"
                    style={{ animationDelay: `${index * 0.03}s` }}
                    data-testid={`open-sale-row-${sale.id}`}
                  >
                    <TableCell>
                      <span className="text-slate-500 text-sm">
                        {formatDateTime(sale.created_at)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <button
                        type="button"
                        onClick={() => handleReprintSale(sale)}
                        disabled={reprintingId === sale.id}
                        className="text-sm font-mono text-emerald-700 hover:text-emerald-900 hover:underline disabled:opacity-50"
                        title="Click para reimprimir este ticket"
                        data-testid={`reprint-ticket-num-${sale.id}`}
                      >
                        {sale.ticket_number || '-'}
                      </button>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">{sale.client_name || 'Sin cliente'}</span>
                      {sale.appointment_id ? (
                        <Badge
                          className="ml-2 bg-emerald-100 text-emerald-800 border border-emerald-200 text-[10px] px-1.5 py-0 font-medium"
                          data-testid={`visit-type-${sale.id}`}
                        >
                          Con cita
                        </Badge>
                      ) : (
                        <Badge
                          className="ml-2 bg-amber-100 text-amber-800 border border-amber-200 text-[10px] px-1.5 py-0 font-medium"
                          data-testid={`visit-type-${sale.id}`}
                        >
                          Walk-in
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">{sale.barber_name || '-'}</span>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant="outline" className="text-slate-600 border-slate-200">
                        {sale.items_count}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            type="button"
                            className="inline-flex items-center gap-1 hover:bg-slate-100 rounded-full transition-colors px-0.5 py-0.5"
                            data-testid={`payment-method-trigger-${sale.id}`}
                            title="Editar método de pago"
                          >
                            <Badge variant="outline" className="text-slate-600 border-slate-200 cursor-pointer">
                              {paymentMethodLabels[sale.payment_method] || sale.payment_method}
                              <span className="ml-1 text-[10px] text-slate-400">▾</span>
                            </Badge>
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48">
                          <DropdownMenuLabel className="text-xs">Cambiar método</DropdownMenuLabel>
                          <DropdownMenuSeparator />
                          {['cash', 'card', 'transfer', 'mixed'].map((key) => {
                            const label = paymentMethodLabels[key] || key;
                            return (
                              <DropdownMenuItem
                                key={key}
                                onClick={() => handleChangePaymentMethod(sale, key)}
                                className={cn(
                                  'cursor-pointer',
                                  sale.payment_method === key && 'bg-slate-100 font-semibold'
                                )}
                                data-testid={`payment-method-option-${sale.id}-${key}`}
                              >
                                {label}
                                {sale.payment_method === key && (
                                  <span className="ml-auto text-xs text-emerald-600">●</span>
                                )}
                              </DropdownMenuItem>
                            );
                          })}
                          <DropdownMenuSeparator />
                          {canEditTotal && !sale.pending_total_authorization && (
                            <DropdownMenuItem
                              onClick={() => openEditTotal(sale)}
                              className="cursor-pointer text-slate-700"
                              data-testid={`edit-total-${sale.id}`}
                            >
                              <DollarSign className="w-3.5 h-3.5 mr-2" />
                              Editar monto…
                            </DropdownMenuItem>
                          )}
                          {canEditTotal && !sale.is_cancelled && !sale.pending_cancel_authorization && !sale.pending_total_authorization && (
                            <DropdownMenuItem
                              onClick={() => openCancelSale(sale)}
                              className="cursor-pointer text-red-600 focus:text-red-700 focus:bg-red-50"
                              data-testid={`cancel-sale-${sale.id}`}
                            >
                              <Trash2 className="w-3.5 h-3.5 mr-2" />
                              Eliminar venta…
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem
                            onClick={() => openAuditLog(sale)}
                            className="cursor-pointer text-slate-600"
                            data-testid={`audit-log-${sale.id}`}
                          >
                            <ScrollText className="w-3.5 h-3.5 mr-2" />
                            Ver auditoría
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                    <TableCell className="text-right">
                      {sale.tip_amount > 0 ? (
                        <div
                          className="flex flex-col items-end"
                          data-testid={`sale-tip-${sale.id}`}
                        >
                          <span
                            className={cn(
                              'font-semibold text-emerald-600',
                              sale.is_cancelled && 'line-through text-slate-400'
                            )}
                          >
                            + {formatCurrency(sale.tip_amount)}
                          </span>
                          {sale.tip_paid_at && (
                            <span
                              className="text-[10px] text-slate-400"
                              title={`Pagada al barbero: ${new Date(sale.tip_paid_at).toLocaleString('es-MX', { timeZone: 'America/Mexico_City' })}`}
                            >
                              pagada
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-slate-300">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <span
                          className={cn(
                            'font-bold',
                            sale.is_cancelled && 'line-through text-slate-400'
                          )}
                        >
                          {formatCurrency(sale.total)}
                        </span>
                        {sale.is_cancelled && (
                          <Badge
                            className="bg-red-100 text-red-800 border-red-200 gap-1"
                            title={
                              (sale.pending_cancel_authorization ? 'Cancelación pendiente de autorización. ' : '') +
                              `Motivo: ${sale.cancel_reason || '—'}`
                            }
                            data-testid={`cancelled-badge-${sale.id}`}
                          >
                            <Trash2 className="w-3 h-3" />
                            {sale.pending_cancel_authorization ? 'Cancel. pend.' : 'Cancelada'}
                          </Badge>
                        )}
                        {sale.pending_total_authorization && (
                          <Badge
                            className="bg-amber-100 text-amber-800 border-amber-200 gap-1"
                            title={`Cambio pendiente de autorización. Anterior: ${formatCurrency(sale.original_total ?? sale.total)}`}
                            data-testid={`pending-auth-badge-${sale.id}`}
                          >
                            <ShieldAlert className="w-3 h-3" /> Pend.
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-slate-500 hover:text-emerald-600 hover:bg-emerald-50"
                        title="Reimprimir ticket"
                        disabled={reprintingId === sale.id}
                        onClick={() => handleReprintSale(sale)}
                        data-testid={`reprint-sale-${sale.id}`}
                      >
                        {reprintingId === sale.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Printer className="w-4 h-4" />
                        )}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Gastos del día */}
      <Card className="border-slate-200 bg-white" data-testid="today-expenses-card">
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <TrendingDown className="w-5 h-5 text-red-500" />
              Gastos del día
            </CardTitle>
            {todayActivitySummary && todayActivitySummary.expenses_count > 0 && (
              <div className="flex items-center gap-4 text-sm">
                <span className="text-slate-500">
                  {todayActivitySummary.expenses_count} {todayActivitySummary.expenses_count === 1 ? 'gasto' : 'gastos'}
                </span>
                <span className="font-bold text-red-600" data-testid="today-expenses-total">
                  {formatCurrency(todayActivitySummary.total_expenses)}
                </span>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {todayExpenses.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-32 text-slate-500" data-testid="today-expenses-empty">
              <TrendingDown className="w-10 h-10 mb-2 opacity-50" />
              <p>No hay gastos registrados hoy</p>
            </div>
          ) : (
            <Table className="data-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Hora</TableHead>
                  <TableHead>Categoría</TableHead>
                  <TableHead>Descripción</TableHead>
                  <TableHead className="text-right">Monto</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {todayExpenses.map((exp, index) => (
                  <TableRow
                    key={exp.id}
                    className="animate-fade-in"
                    style={{ animationDelay: `${index * 0.03}s` }}
                    data-testid={`today-expense-row-${exp.id}`}
                  >
                    <TableCell>
                      <span className="text-slate-500 text-sm">
                        {formatDateTime(exp.created_at)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-slate-600 border-slate-200 capitalize">
                        {exp.category || 'otro'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">{exp.description || '-'}</span>
                    </TableCell>
                    <TableCell className="text-right">
                      <span className="font-bold text-red-500">- {formatCurrency(exp.amount)}</span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Depósitos y retiros del día */}
      <Card className="border-slate-200 bg-white" data-testid="today-movements-card">
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <Wallet className="w-5 h-5" />
              Depósitos y retiros del día
            </CardTitle>
            {todayActivitySummary && (todayActivitySummary.deposits_count + todayActivitySummary.withdrawals_count) > 0 && (
              <div className="flex items-center gap-4 text-sm">
                <span className="text-emerald-600 font-bold" data-testid="today-deposits-total">
                  + {formatCurrency(todayActivitySummary.total_deposits)}
                </span>
                <span className="text-red-500 font-bold" data-testid="today-withdrawals-total">
                  - {formatCurrency(todayActivitySummary.total_withdrawals)}
                </span>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {todayMovements.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-32 text-slate-500" data-testid="today-movements-empty">
              <Wallet className="w-10 h-10 mb-2 opacity-50" />
              <p>No hay depósitos ni retiros registrados hoy</p>
            </div>
          ) : (
            <Table className="data-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Hora</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Descripción</TableHead>
                  <TableHead className="text-right">Monto</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {todayMovements.map((mv, index) => {
                  const isDeposit = mv.type === 'deposit';
                  return (
                    <TableRow
                      key={mv.id}
                      className="animate-fade-in"
                      style={{ animationDelay: `${index * 0.03}s` }}
                      data-testid={`today-movement-row-${mv.id}`}
                    >
                      <TableCell>
                        <span className="text-slate-500 text-sm">
                          {formatDateTime(mv.created_at)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={cn(
                            isDeposit
                              ? 'text-emerald-600 border-emerald-500/30'
                              : 'text-red-500 border-red-500/30'
                          )}
                        >
                          {isDeposit ? 'Depósito' : 'Retiro'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">{mv.description || '-'}</span>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className={cn('font-bold', isDeposit ? 'text-emerald-600' : 'text-red-500')}>
                          {isDeposit ? '+ ' : '- '}{formatCurrency(Math.abs(mv.amount || 0))}
                        </span>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Movements */}
      <Card className="border-slate-200 bg-white">
        <CardHeader>
          <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
            <History className="w-5 h-5" />
            Movimientos del día
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {movements.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 text-slate-500">
              <History className="w-12 h-12 mb-3 opacity-50" />
              <p>No hay movimientos</p>
            </div>
          ) : (
            <Table className="data-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Hora</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Descripción</TableHead>
                  <TableHead>Monto</TableHead>
                  <TableHead>Saldo</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {movements.map((movement, index) => {
                  const isPositive = movement.amount > 0;
                  return (
                    <TableRow 
                      key={movement.id}
                      className="animate-fade-in"
                      style={{ animationDelay: `${index * 0.03}s` }}
                    >
                      <TableCell>
                        <span className="text-slate-500 text-sm">
                          {formatDateTime(movement.created_at)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Badge 
                          variant="outline" 
                          className={cn(
                            isPositive 
                              ? "text-emerald-400 border-emerald-500/30" 
                              : "text-red-400 border-red-500/30"
                          )}
                        >
                          {movement.type === 'opening' && 'Apertura'}
                          {movement.type === 'closing' && 'Cierre'}
                          {movement.type === 'sale' && 'Venta'}
                          {movement.type === 'withdrawal' && 'Retiro'}
                          {movement.type === 'deposit' && 'Depósito'}
                          {movement.type === 'expense' && 'Gasto'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">{movement.description || '-'}</span>
                      </TableCell>
                      <TableCell>
                        <span className={cn(
                          "font-bold",
                          isPositive ? "text-emerald-400" : "text-red-400"
                        )}>
                          {isPositive ? '+' : ''}{formatCurrency(movement.amount)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="font-medium">{formatCurrency(movement.balance_after)}</span>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Open Register Dialog */}
      <Dialog open={openDialog} onOpenChange={setOpenDialog}>
        <DialogContent className="bg-white border-slate-200">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>Abrir caja</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Monto inicial</Label>
              <Input
                type="number"
                value={openingAmount}
                onChange={(e) => setOpeningAmount(parseFloat(e.target.value) || 0)}
                className="bg-slate-100 border-slate-200"
                min={0}
                step={0.01}
              />
            </div>

            <div className="space-y-2">
              <Label>Notas (opcional)</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="bg-slate-100 border-slate-200 resize-none"
                placeholder="Notas de apertura..."
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenDialog(false)} className="border-slate-200">
              Cancelar
            </Button>
            <Button onClick={handleOpenRegister} className="bg-emerald-600 hover:bg-emerald-700 text-white">
              Abrir caja
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Close Register Dialog */}
      <Dialog open={closeDialog} onOpenChange={setCloseDialog}>
        <DialogContent className="bg-white border-slate-200">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>Cerrar caja</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="p-4 rounded-lg bg-slate-50">
              <div className="flex justify-between items-center">
                <span className="text-slate-500">Monto esperado:</span>
                <span className="text-xl font-bold">{formatCurrency(selectedRegister?.expected_amount || 0)}</span>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Monto contado</Label>
              <Input
                type="number"
                value={closingAmount}
                onChange={(e) => setClosingAmount(parseFloat(e.target.value) || 0)}
                className="bg-slate-100 border-slate-200"
                min={0}
                step={0.01}
              />
            </div>

            {closingAmount !== selectedRegister?.expected_amount && (
              <div className={cn(
                "p-3 rounded-lg flex items-center gap-2",
                closingAmount > (selectedRegister?.expected_amount || 0)
                  ? "bg-emerald-500/10 text-emerald-400"
                  : "bg-red-500/10 text-red-400"
              )}>
                <AlertTriangle className="w-4 h-4" />
                <span>
                  Diferencia: {formatCurrency(closingAmount - (selectedRegister?.expected_amount || 0))}
                </span>
              </div>
            )}

            <div className="space-y-2">
              <Label>Notas (opcional)</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="bg-slate-100 border-slate-200 resize-none"
                placeholder="Notas de cierre..."
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCloseDialog(false)} className="border-slate-200">
              Cancelar
            </Button>
            <Button onClick={handleCloseRegister} className="bg-red-600 hover:bg-red-700 text-white">
              Cerrar caja
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Movement Dialog */}
      <Dialog open={movementDialog} onOpenChange={setMovementDialog}>
        <DialogContent className="bg-white border-slate-200">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>
              {movementType === 'withdrawal' ? 'Registrar retiro' : 'Registrar depósito'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Monto</Label>
              <Input
                type="number"
                value={movementAmount}
                onChange={(e) => setMovementAmount(parseFloat(e.target.value) || 0)}
                className="bg-slate-100 border-slate-200"
                min={0}
                step={0.01}
              />
            </div>

            <div className="space-y-2">
              <Label>Descripción</Label>
              <Input
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="bg-slate-100 border-slate-200"
                placeholder="Razón del movimiento..."
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setMovementDialog(false)} className="border-slate-200">
              Cancelar
            </Button>
            <Button onClick={handleMovement} className="bg-primary text-white hover:bg-primary/90">
              Registrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Audit log dialog */}
      <Dialog open={!!auditSale} onOpenChange={(o) => !o && setAuditSale(null)}>
        <DialogContent className="bg-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ScrollText className="w-5 h-5" />
              Auditoría de la venta
            </DialogTitle>
          </DialogHeader>

          {auditSale && (
            <div className="text-xs text-slate-500 mb-2">
              Ticket <span className="font-mono text-slate-700">{auditSale.ticket_number}</span>
              {' · '}
              {auditSale.client_name || 'Sin cliente'}
              {' · '}
              Total <span className="font-semibold text-slate-700">{formatCurrency(auditSale.total)}</span>
            </div>
          )}

          {auditLoading ? (
            <div className="flex items-center justify-center py-12 text-slate-400">
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
              Cargando bitácora…
            </div>
          ) : auditEntries.length === 0 ? (
            <div className="py-12 text-center text-slate-400 text-sm" data-testid="audit-empty">
              Esta venta no tiene cambios registrados.
            </div>
          ) : (
            <div className="max-h-96 overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Fecha</TableHead>
                    <TableHead>Usuario</TableHead>
                    <TableHead>Cambio</TableHead>
                    <TableHead>Caja</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {auditEntries.map((a) => {
                    const isTotalChange = ['total_requested', 'total_approved', 'total_rejected'].includes(a.field);
                    return (
                      <TableRow key={a.id} data-testid={`audit-entry-${a.id}`}>
                        <TableCell className="text-xs">
                          {formatDateTime(a.created_at)}
                        </TableCell>
                        <TableCell className="text-sm">
                          {a.changed_by_name || a.changed_by || '—'}
                        </TableCell>
                        <TableCell className="text-sm">
                          {isTotalChange ? (
                            <div className="space-y-0.5">
                              <div className="flex items-center gap-1">
                                {a.field === 'total_requested' && (
                                  <Badge className="bg-amber-100 text-amber-800 border-amber-200 gap-1">
                                    <ShieldAlert className="w-3 h-3" /> Cambio solicitado
                                  </Badge>
                                )}
                                {a.field === 'total_approved' && (
                                  <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 gap-1">
                                    <ShieldCheck className="w-3 h-3" /> Aprobado
                                  </Badge>
                                )}
                                {a.field === 'total_rejected' && (
                                  <Badge className="bg-red-100 text-red-800 border-red-200 gap-1">
                                    <ShieldX className="w-3 h-3" /> Rechazado
                                  </Badge>
                                )}
                              </div>
                              <div className="text-xs">
                                <span className="line-through text-slate-400">{formatCurrency(a.old_value)}</span>
                                <span className="mx-1">→</span>
                                <span className="font-bold text-slate-700">{formatCurrency(a.new_value)}</span>
                              </div>
                              {a.reason && (
                                <div className="text-[11px] text-slate-500 italic">&ldquo;{a.reason}&rdquo;</div>
                              )}
                              {a.notes && (
                                <div className="text-[11px] text-slate-500">Nota: {a.notes}</div>
                              )}
                            </div>
                          ) : (
                            <>
                              <span className="text-slate-500">{a.field}:</span>{' '}
                              <Badge variant="outline" className="text-slate-600 border-slate-200 mr-1">
                                {paymentMethodLabels[a.old_value] || a.old_value}
                              </Badge>
                              →
                              <Badge variant="outline" className="text-emerald-700 border-emerald-200 ml-1">
                                {paymentMethodLabels[a.new_value] || a.new_value}
                              </Badge>
                            </>
                          )}
                        </TableCell>
                        <TableCell className="text-xs">
                          {a.register_adjustment === '+cash' && (
                            <span className="text-emerald-600">+ {formatCurrency(a.amount)} efectivo</span>
                          )}
                          {a.register_adjustment === '-cash' && (
                            <span className="text-red-500">- {formatCurrency(a.amount)} efectivo</span>
                          )}
                          {a.register_adjustment === 'none' && (
                            <span className="text-slate-400">sin ajuste</span>
                          )}
                          {isTotalChange && !a.register_adjustment && (
                            <span className="text-slate-400">—</span>
                          )}
                          {!a.register_was_open && a.register_adjustment && a.register_adjustment !== 'none' && (
                            <p className="text-[10px] text-amber-600 mt-0.5">caja cerrada</p>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setAuditSale(null)} className="border-slate-200">
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Total Dialog (admin request) */}
      <Dialog open={!!editTotalSale} onOpenChange={(o) => !o && closeEditTotal()}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5" />
              Editar monto cobrado
            </DialogTitle>
          </DialogHeader>

          {editTotalSale && (
            <div className="space-y-4 py-2">
              <div className="p-3 rounded-md bg-slate-50 border border-slate-200 text-sm space-y-1">
                <div className="flex justify-between">
                  <span className="text-slate-500">Ticket</span>
                  <span className="font-mono">{editTotalSale.ticket_number}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Cliente</span>
                  <span>{editTotalSale.client_name || 'Sin cliente'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Total actual</span>
                  <span className="font-bold">{formatCurrency(editTotalSale.total)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Método</span>
                  <span>{paymentMethodLabels[editTotalSale.payment_method] || editTotalSale.payment_method}</span>
                </div>
              </div>

              <div className="p-3 rounded-md bg-amber-50 border border-amber-200 text-xs text-amber-800 flex gap-2">
                <ShieldAlert className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>
                  El nuevo monto se aplicará <strong>de inmediato</strong> a la venta y a la caja
                  (si es efectivo), pero <strong>quedará pendiente</strong> hasta que el master admin
                  autorice. Las comisiones sólo se recalcularán al aprobar.
                </span>
              </div>

              <div className="space-y-2">
                <Label>Nuevo monto</Label>
                <Input
                  type="number"
                  value={newTotalInput}
                  onChange={(e) => setNewTotalInput(e.target.value)}
                  className="bg-slate-100 border-slate-200"
                  min={0}
                  step={0.01}
                  data-testid="edit-total-amount-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Motivo del cambio <span className="text-red-500">*</span></Label>
                <Textarea
                  value={reasonInput}
                  onChange={(e) => setReasonInput(e.target.value)}
                  className="bg-slate-100 border-slate-200 resize-none"
                  placeholder="Ej. Cliente cuestionó el cobro, descuento no aplicado, etc."
                  rows={3}
                  data-testid="edit-total-reason-input"
                />
                <p className="text-[11px] text-slate-500">
                  Este motivo queda registrado en la auditoría y visible para el master admin.
                </p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={closeEditTotal} disabled={editSubmitting}>
              Cancelar
            </Button>
            <Button
              onClick={submitEditTotal}
              disabled={editSubmitting}
              className="bg-primary text-white hover:bg-primary/90"
              data-testid="edit-total-submit-btn"
            >
              {editSubmitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Solicitar cambio
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Sale Dialog (admin request; master admin authorization) */}
      <Dialog open={!!cancelSale} onOpenChange={(o) => !o && closeCancelSale()}>
        <DialogContent
          className="bg-white max-w-md"
          data-testid="cancel-sale-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <Trash2 className="w-5 h-5" />
              Eliminar venta
            </DialogTitle>
          </DialogHeader>

          {cancelSale && (
            <div className="space-y-4 py-2">
              <div className="p-3 rounded-md bg-slate-50 border border-slate-200 text-sm space-y-1">
                <div className="flex justify-between">
                  <span className="text-slate-500">Ticket</span>
                  <span className="font-mono">{cancelSale.ticket_number}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Cliente</span>
                  <span>{cancelSale.client_name || 'Sin cliente'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Total</span>
                  <span className="font-bold">{formatCurrency(cancelSale.total)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Método</span>
                  <span>{paymentMethodLabels[cancelSale.payment_method] || cancelSale.payment_method}</span>
                </div>
              </div>

              <div className="p-3 rounded-md bg-red-50 border border-red-200 text-xs text-red-800 flex gap-2">
                <ShieldAlert className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>
                  Al confirmar, la venta se marcará como <strong>cancelada</strong> de inmediato,
                  se devolverá el <strong>stock</strong> al inventario, se anularán las
                  <strong> comisiones</strong> y <strong>sellos de lealtad</strong>, y se ajustará
                  la <strong>caja</strong> (si fue en efectivo). La acción queda
                  <strong> pendiente de autorización</strong> del master admin y todo el motivo
                  queda registrado para auditoría.
                </span>
              </div>

              <div className="space-y-2">
                <Label htmlFor="cancel-reason-textarea">
                  Motivo <span className="text-red-600">*</span>
                </Label>
                <Textarea
                  id="cancel-reason-textarea"
                  rows={3}
                  value={cancelReasonInput}
                  onChange={(e) => setCancelReasonInput(e.target.value)}
                  placeholder="Ej.: Venta duplicada por error de captura"
                  className="bg-slate-50"
                  data-testid="cancel-reason-input"
                />
                <p className="text-[10px] text-slate-500">
                  Mínimo 3 caracteres. Este motivo aparecerá en el historial de auditoría.
                </p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={closeCancelSale}
              disabled={cancelSubmitting}
              data-testid="cancel-sale-close-btn"
            >
              Cerrar
            </Button>
            <Button
              onClick={submitCancelSale}
              disabled={cancelSubmitting}
              className="bg-red-600 text-white hover:bg-red-700"
              data-testid="cancel-sale-submit-btn"
            >
              {cancelSubmitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Confirmar eliminación
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
